import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, X } from 'lucide-react';
import { motion } from 'framer-motion';
import { useMaxWidth } from '../../utils/useMaxWidth';

const progressTrackStyle = {
  height: 6,
  borderRadius: 999,
  background: 'var(--color-bg)',
  border: '1px solid var(--color-border)',
  overflow: 'hidden',
};

function ProgressDots({ stepIndex, totalSteps }) {
  const n = Math.max(0, Math.floor(Number(totalSteps)) || 0);
  const cur = Math.min(Math.max(0, Math.floor(Number(stepIndex)) || 0), Math.max(0, n - 1));
  if (n <= 0) return null;
  const dense = n > 24;
  const size = dense ? 5 : 7;
  const gap = dense ? 3 : 5;
  const dots = Array.from({ length: n }, (_, i) => {
    const filled = i <= cur;
    return (
      <span
        key={i}
        title={`Step ${i + 1}`}
        style={{
          width: size,
          height: size,
          borderRadius: 999,
          flexShrink: 0,
          background: filled ? 'var(--color-primary)' : 'var(--color-border)',
          opacity: filled ? 1 : 0.55,
          boxShadow: filled ? '0 0 0 1px rgba(0,0,0,0.06)' : 'none',
        }}
        aria-hidden
      />
    );
  });
  return (
    <div
      role="img"
      aria-label={`Progress: step ${cur + 1} of ${n}`}
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        alignItems: 'center',
        gap,
        marginTop: 6,
        marginBottom: 2,
        maxWidth: '100%',
      }}
    >
      {dots}
    </div>
  );
}

/**
 * Full-viewport guided lesson layout: header + progress, main column, optional floating panel above fixed bottom bar.
 *
 * @param {object} props
 * @param {string} props.backToWorld – route href
 * @param {string} props.worldTitle – short label next to back control
 * @param {string} [props.lessonTitle] – lesson name (shown under world title in minimal mode, or under meta in default)
 * @param {number} props.stepIndex – 0-based
 * @param {number} props.totalSteps
 * @param {string} [props.metaLine] – e.g. age band and duration (desktop-friendly context)
 * @param {'default'|'minimal'} [props.headerMode] – `minimal` hides meta/points and uses a compact close + progress row (in-progress activities)
 * @param {'lessonScroll'|'locked'} [props.scrollMode] – `locked` disables main-column scrolling (tap-first lessons)
 * @param {string} props.mainPaddingBottom – CSS length for main region bottom padding (clears bar + float + safe area)
 * @param {React.ReactNode} [props.floatingPanel] – anchored above bottom bar (hints / why)
 * @param {string} [props.floatingBottomOffset] – CSS distance from viewport bottom to floating panel anchor (above action bar)
 * @param {React.ReactNode} props.bottomBar – fixed lesson actions
 * @param {React.ReactNode} props.children – main step content (max-width column centered inside shell)
 */
export default function FocusedLessonShell({
  backToWorld,
  worldTitle,
  lessonTitle,
  stepIndex,
  totalSteps,
  metaLine,
  headerMode = 'default',
  scrollMode = 'lessonScroll',
  mainPaddingBottom,
  floatingPanel = null,
  floatingBottomOffset = 'calc(88px + env(safe-area-inset-bottom, 0px))',
  bottomBar,
  headerAction = null,
  children,
}) {
  const safeTop = 'env(safe-area-inset-top, 0px)';
  const minimal = headerMode === 'minimal';
  const narrow = useMaxWidth(520);
  const locked = scrollMode === 'locked';

  const padTop = narrow
    ? safeTop
    : minimal
      ? `max(4px, ${safeTop})`
      : `max(8px, ${safeTop})`;
  const headerPad = narrow
    ? minimal
      ? '0 8px 4px'
      : '0 10px 8px'
    : minimal
      ? '0 10px 6px'
      : '0 14px 10px';
  const headerRowMin = narrow ? (minimal ? 40 : 40) : minimal ? 40 : 40;
  const mainPad = narrow ? '4px 10px 10px' : '8px 14px 16px';
  const barPad = narrow ? '8px 10px calc(8px + env(safe-area-inset-bottom, 0px))' : '8px 14px calc(10px + env(safe-area-inset-bottom, 0px))';

  const mainColumnStyle = {
    flex: 1,
    minHeight: 0,
    paddingBottom: mainPaddingBottom,
    boxSizing: 'border-box',
    ...(locked
      ? {
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }
      : {
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          overscrollBehaviorY: 'contain',
          display: 'flex',
          flexDirection: 'column',
        }),
  };

  const mainInnerStyle = {
    width: '100%',
    maxWidth: 720,
    margin: '0 auto',
    padding: mainPad,
    boxSizing: 'border-box',
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    ...(locked
      ? {
          overflow: 'hidden',
        }
      : {}),
  };

  return (
    <div
      style={{
        height: '100dvh',
        minHeight: '100vh',
        maxHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--color-bg)',
        boxSizing: 'border-box',
        overflow: 'hidden',
        paddingTop: padTop,
      }}
    >
      <header
        style={{
          flexShrink: 0,
          width: '100%',
          maxWidth: 720,
          margin: '0 auto',
          padding: headerPad,
          boxSizing: 'border-box',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: minimal ? 8 : 10,
            minHeight: headerRowMin,
          }}
        >
          <Link
            to={backToWorld}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: minimal ? 0 : 4,
              fontWeight: 600,
              fontSize: 14,
              color: minimal ? 'var(--color-text)' : 'var(--color-primary)',
              minWidth: minimal ? 44 : 0,
              width: minimal ? 44 : 'auto',
              flex: minimal ? '0 0 auto' : 1,
              minHeight: minimal ? 44 : 'auto',
              borderRadius: minimal ? 12 : 0,
              textDecoration: 'none',
            }}
            aria-label={minimal ? `Close lesson and return to ${worldTitle}` : `Back to ${worldTitle}`}
          >
            {minimal ? (
              <X size={22} strokeWidth={2.2} aria-hidden />
            ) : (
              <>
                <ChevronLeft size={20} aria-hidden style={{ flexShrink: 0 }} />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {worldTitle}
                </span>
              </>
            )}
          </Link>
          {minimal ? (
            lessonTitle ? (
              <p
                style={{
                  margin: 0,
                  flex: 1,
                  minWidth: 0,
                  marginLeft: 6,
                  marginRight: 8,
                  fontSize: 12,
                  fontWeight: 700,
                  lineHeight: 1.25,
                  color: 'var(--color-text)',
                  opacity: 0.88,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={lessonTitle}
              >
                {lessonTitle}
              </p>
            ) : (
              <span style={{ flex: 1, minWidth: 0 }} aria-hidden />
            )
          ) : null}
          {!minimal ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  opacity: 0.72,
                  color: 'var(--color-text-soft, var(--color-text))',
                  whiteSpace: 'nowrap',
                }}
                aria-hidden
              >
                Points · —
              </span>
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 800,
                  opacity: 0.88,
                  padding: '5px 11px',
                  borderRadius: 999,
                  background: 'var(--color-bg-card)',
                  border: '1px solid var(--color-border)',
                  boxShadow: '0 1px 6px var(--color-shadow)',
                }}
                aria-label={`Step ${stepIndex + 1} of ${totalSteps}`}
              >
                {stepIndex + 1}/{totalSteps}
              </span>
              {headerAction}
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 800,
                  opacity: 0.88,
                  padding: '6px 11px',
                  borderRadius: 999,
                  background: 'var(--color-bg-card)',
                  border: '1px solid var(--color-border)',
                }}
                aria-label={`Step ${stepIndex + 1} of ${totalSteps}`}
              >
                {stepIndex + 1}/{totalSteps}
              </span>
              {headerAction}
            </div>
          )}
        </div>
        {!minimal && lessonTitle ? (
          <p
            style={{
              margin: '4px 0 0',
              fontSize: 12,
              fontWeight: 700,
              lineHeight: 1.3,
              color: 'var(--color-text)',
              opacity: 0.85,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={lessonTitle}
          >
            {lessonTitle}
          </p>
        ) : null}
        {!minimal && metaLine ? (
          <p
            style={{
              margin: '4px 0 6px',
              fontSize: 12,
              opacity: 0.75,
              lineHeight: 1.4,
              color: 'var(--color-text)',
            }}
          >
            {metaLine}
          </p>
        ) : null}
        <ProgressDots stepIndex={stepIndex} totalSteps={totalSteps} />
        <div
          style={{
            ...progressTrackStyle,
            marginTop: narrow ? 4 : minimal ? 6 : 4,
            height: narrow ? 4 : minimal ? 5 : 6,
          }}
          role="progressbar"
          aria-valuenow={stepIndex + 1}
          aria-valuemin={1}
          aria-valuemax={totalSteps}
        >
          <motion.div
            initial={false}
            animate={{ width: `${totalSteps ? ((stepIndex + 1) / totalSteps) * 100 : 0}%` }}
            transition={{ type: 'spring', stiffness: 220, damping: 28 }}
            style={{
              height: '100%',
              background: 'var(--color-primary)',
              opacity: minimal ? 1 : 0.9,
            }}
          />
        </div>
      </header>

      <div style={mainColumnStyle} className={locked ? undefined : 'hide-scrollbar'}>
        <div style={mainInnerStyle}>{children}</div>
      </div>

      {floatingPanel ? (
        <div
          style={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: floatingBottomOffset,
            zIndex: 99,
            maxWidth: 720,
            margin: '0 auto',
            padding: '0 14px',
            boxSizing: 'border-box',
            pointerEvents: 'none',
          }}
        >
          <div style={{ pointerEvents: 'auto' }}>{floatingPanel}</div>
        </div>
      ) : null}

      <div
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          maxWidth: 720,
          margin: '0 auto',
          padding: barPad,
          boxSizing: 'border-box',
          background: 'var(--color-surface, var(--color-bg-card))',
          borderTop: '1px solid var(--color-border)',
          boxShadow: '0 -6px 28px var(--color-shadow)',
        }}
        role="region"
        aria-label="Lesson actions"
      >
        {bottomBar}
      </div>
    </div>
  );
}
